import { createTableDefaultColumn } from '@common/tableColumn.services';
import { Badge, Button, ComponentType, ModalBodyExtras, PaginatedTable, useModal } from '@gcl/react-core';
import {
  IProjectLoadingRemovalCert,
  IProjectLoadingRemovalCertBase,
} from '@permitToLoadRemove/detail/permitToLoadRemoveDetailPage.models';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { IRevision } from '@revision/revision.models';
import { isSuperseded } from '@revision/revision.services';
import { isSupersededT4 } from '@permitToLoadRemove/detail/permitToLoadRemovalDetailPage.hooks';
import { ESelectRevisionModalType } from './SelectRevisionModal.constants';

export interface IUseSelectRevisionModalProps<T> {
  type: ESelectRevisionModalType;
  revisions?: T[];
  currentT4?: IProjectLoadingRemovalCert;
}

export const useSelectRevisionListModal: <T extends object>(
  props: IUseSelectRevisionModalProps<T>,
) => {
  openPortal: any;
  closePortal: any;
  renderModal: () => React.ReactElement;
  badge: React.ReactElement | null;
} = ({ type, revisions = [], currentT4 }) => {
  const { openPortal, closePortal, renderModal } = useModal();
  const { navigate } = useNavigate();

  const revisionTableColumns = React.useMemo(() => {
    if (type === ESelectRevisionModalType.T2 || type === ESelectRevisionModalType.T3) {
      const title = type === ESelectRevisionModalType.T2 ? 'Design Brief No.' : 'Design Modification No.';
      const route = type === ESelectRevisionModalType.T2 ? routes.t2.view.path : routes.t3.view.path;

      return [
        createTableDefaultColumn<IRevision>(
          'formNumber',
          title,
          ({ value, item }) => {
            if (item.isCurrent) {
              closePortal();
              return;
            }
            navigate(route, { id: item.formId });
            closePortal();
          },
          ({ item }) => {
            return 'gcl-fl-md';
          },
          ({ item }) => {
            return { cursor: 'pointer' };
          },
        ),
      ];
    }
    if (type === ESelectRevisionModalType.T4)
      return [
        createTableDefaultColumn<IProjectLoadingRemovalCertBase>(
          'certificateNo',
          'Certificate No.',
          ({ value, item }) => {
            navigate(routes.t4.view.path, { id: item.id });
            closePortal();
          },
          ({ item }) => {
            return 'gcl-fl-md';
          },
          ({ item }) => {
            return { cursor: 'pointer' };
          },
        ),
      ];

    return [];
  }, []);

  const badge = React.useMemo(() => {
    if ((revisions?.length ?? []) <= 1) {
      return null;
    }
    const isSupersededLocal =
      type === ESelectRevisionModalType.T4 ? isSupersededT4(currentT4) : isSuperseded(revisions as IRevision[]);
    return (
      <Badge
        pill
        type={ComponentType.Secondary}
        className="gcl-ml-sm gcl-fb-md gcl-fc-greyscale-100 gcl-clickable"
        onClick={() => {
          openPortal();
        }}
      >
        {!isSupersededLocal ? 'Earlier versions' : 'Superseded'}
      </Badge>
    );
  }, [JSON.stringify(revisions)]);

  const renderRevisionListModal = () => {
    return renderModal({
      large: true,
      title: `${type} Revision List`,
      onRenderBody: (bodyExtras?: ModalBodyExtras) => {
        return (
          <div className="gcl-gems-revision-list-table">
            <PaginatedTable
              classNames={{ row: 'gcl-gems-revision-list-table-row' }}
              columns={revisionTableColumns}
              items={revisions || []}
              disablePagination
              allowSelectRow={false}
            />
          </div>
        );
      },
      onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
        return (
          <Button
            type={ComponentType.Secondary}
            onClick={() => {
              if (onHide) {
                onHide();
              }
            }}
            style={{ margin: '10px' }}
          >
            Close
          </Button>
        );
      },
    });
  };
  return {
    openPortal,
    closePortal,
    renderModal: renderRevisionListModal,
    badge,
  };
};
